import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { fmtAestDate, todayAestISO, addDaysAestISO } from "../../lib/format";
import { CsvButton, PrintButton } from "../../components/ReportButtons";

const TYPE_PILL = {
  TASK: "pill-blue",
  NOTE: "pill-green",
  SALES_ACTIVITY: "pill-yellow",
  DISCOVERY: "pill-purple",
};

export default function SalesActivity() {
  const { meta } = useAuth();
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = todayAestISO();
  // 30 days ago in AEST - simpler and still matches the previous "monthAgo" intent.
  const monthAgo = addDaysAestISO(-30);
  const [f, setF] = useState({
    rep_id: "", company_id: "", state: "", country: "",
    segment: "", activity_type: "",
    from_date: monthAgo,
    to_date: today,
  });
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    api.get("/users").then(r => setUsers(r.data)).catch(() => {});
    api.get("/companies?limit=2000").then(r => setCompanies(r.data)).catch(() => {});
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      const { data } = await api.get("/reports/sales-activity", { params });
      setRows(data.rows);
    } finally { setLoading(false); }
  }, [f]);
  // Run on mount only - filters are applied via the "Apply filters" button.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, []);

  const csvParams = useMemo(() => ({ ...f, format: "csv" }), [f]);

  return (
    <div className="space-y-4" data-testid="sales-activity-page">
      <div className="flex flex-wrap items-center justify-between gap-2 print-hide">
        <div>
          <h1 className="text-2xl font-bold">Sales Activity</h1>
          <div className="text-sm text-gray-500">{rows.length} record{rows.length !== 1 ? "s" : ""}</div>
        </div>
        <div className="flex items-center gap-2">
          <CsvButton path="/reports/sales-activity" params={csvParams} testId="sa-csv"/>
          <PrintButton testId="sa-print"/>
        </div>
      </div>

      {/* Filters */}
      <div className="cesco-card p-4 print-hide">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div><label className="cesco-label">From</label>
            <input type="date" className="cesco-input" value={f.from_date}
                   onChange={e => setF({ ...f, from_date: e.target.value })} data-testid="sa-from"/></div>
          <div><label className="cesco-label">To</label>
            <input type="date" className="cesco-input" value={f.to_date}
                   onChange={e => setF({ ...f, to_date: e.target.value })} data-testid="sa-to"/></div>
          <div><label className="cesco-label">Rep</label>
            <select className="cesco-input" value={f.rep_id}
                    onChange={e => setF({ ...f, rep_id: e.target.value })} data-testid="sa-rep">
              <option value="">All reps</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select></div>
          <div><label className="cesco-label">Company</label>
            <select className="cesco-input" value={f.company_id}
                    onChange={e => setF({ ...f, company_id: e.target.value })} data-testid="sa-company">
              <option value="">All companies</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select></div>
          <div><label className="cesco-label">State</label>
            <select className="cesco-input" value={f.state}
                    onChange={e => setF({ ...f, state: e.target.value })} data-testid="sa-state">
              <option value="">All states</option>
              {meta?.states?.map(s => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div><label className="cesco-label">Country</label>
            <input className="cesco-input" value={f.country}
                   onChange={e => setF({ ...f, country: e.target.value })}
                   placeholder="e.g. Australia" data-testid="sa-country"/></div>
          <div><label className="cesco-label">Segment</label>
            <select className="cesco-input" value={f.segment}
                    onChange={e => setF({ ...f, segment: e.target.value })} data-testid="sa-segment">
              <option value="">All segments</option>
              {meta?.product_segments?.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select></div>
          <div><label className="cesco-label">Type</label>
            <select className="cesco-input" value={f.activity_type}
                    onChange={e => setF({ ...f, activity_type: e.target.value })} data-testid="sa-type">
              <option value="">Conversation / Activity</option>
              <option value="SALES_ACTIVITY">Sales activities only</option>
              <option value="TASK">Tasks only</option>
              <option value="NOTE">Notes only</option>
            </select></div>
        </div>
        <div className="mt-3"><button className="cesco-btn-primary" onClick={reload}
                                       data-testid="sa-apply">Apply filters</button></div>
      </div>

      {/* Data */}
      <div className="cesco-card overflow-x-auto print-region">
        <table className="w-full text-sm" data-testid="sa-table">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Rep</th>
              <th className="text-left px-3 py-2">Company</th>
              <th className="text-left px-3 py-2">State</th>
              <th className="text-left px-3 py-2">Drill Market</th>
              <th className="text-left px-3 py-2">Contact</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Sub-type</th>
              <th className="text-left px-3 py-2">Summary</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="p-4 text-center text-gray-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="p-4 text-center text-gray-500">No activity in the selected range</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={`${r._kind}-${i}`} className="border-t">
                <td className="px-3 py-2 whitespace-nowrap">{fmtAestDate(r._when)}</td>
                <td className="px-3 py-2">{r.rep_name || "-"}</td>
                <td className="px-3 py-2">
                  {r.company_id
                    ? <Link className="hover:underline" to={`/companies/${r.company_id}`}>{r.company_name}</Link>
                    : r.company_name}
                </td>
                <td className="px-3 py-2">{r.state}</td>
                <td className="px-3 py-2 text-xs">{r.drill_market_segment || "-"}</td>
                <td className="px-3 py-2 text-xs">{r.contact_name || "-"}</td>
                <td className="px-3 py-2"><span className={`pill ${TYPE_PILL[r.type] || "pill-grey"}`}>{r.type}</span></td>
                <td className="px-3 py-2 text-xs">{r.sub_type}</td>
                <td className="px-3 py-2">{r.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
